import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Download, Share2, Facebook, Loader2, Trophy, Star } from "lucide-react";
import { toast } from "sonner";
import { BOOKS } from "@/lib/catalog";
import { renderFinishedCard, renderCompletionistCard } from "@/lib/finishedCard";
import { readerMe } from "@/lib/reader";

export default function FinishedDialog({ productId, name: givenName, finishedAt, onClose, completionist = false }) {
  const meta = BOOKS.find((b) => b.id === productId);
  const [url, setUrl] = useState(null);
  const [name, setName] = useState(givenName);
  useEffect(() => {
    if (givenName === undefined) readerMe().then((m) => setName(m.user.name || null)).catch(() => setName(null));
  }, [givenName]);
  useEffect(() => {
    if (name === undefined) return;
    const render = completionist ? renderCompletionistCard : renderFinishedCard;
    render({ productId, name, finishedAt }).then(setUrl).catch(() => toast.error("Could not create your card."));
  }, [productId, name, finishedAt, completionist]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const fileName = completionist ? "leckie-completionist.png" : `i-finished-${productId}.png`;
  const shareTitle = completionist ? "Leckie Completionist" : `I finished ${meta?.fullTitle}`;
  const shareText = completionist ? "I've read all four Michael Leckie novels!" : `I just finished ${meta?.fullTitle} by Michael Leckie!`;
  const share = async () => {
    try {
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: shareTitle, text: shareText });
      } else {
        toast.info("Sharing images isn't supported here — download the card instead.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not share the card.");
    }
  };
  const facebook = () => {
    const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/${productId}`;
    const popup = window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "fb-share", "width=640,height=560");
    if (!popup) toast.error("Please allow pop-ups to share on Facebook.");
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="finished-dialog">
      <div className="relative w-full max-w-md bg-[#121212] border border-primary-gold/40 p-6 sm:p-8 fade-up my-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" data-testid="finished-close" className="absolute top-4 right-4 text-soft hover:text-text-primary"><X className="w-5 h-5" /></button>
        <p className="flex items-center gap-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-primary-gold"><Trophy className="w-4 h-4" /> {completionist ? "All four books finished" : "Book finished"}</p>
        {completionist ? (
          <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 leading-tight" data-testid="completionist-title">You're a <span className="text-primary-gold italic">Leckie Completionist</span>!</h2>
        ) : (
          <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 leading-tight">You finished <span className="text-primary-gold italic">{meta?.fullTitle}</span>!</h2>
        )}
        <div className="mt-6">
          {url ? (
            <img src={url} alt={shareTitle} className="w-full border border-white/10" data-testid="finished-card-image" />
          ) : (
            <div className="aspect-[4/5] flex items-center justify-center border border-white/10"><Loader2 className="w-5 h-5 text-primary-gold animate-spin" /></div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-5">
          <a href={url || undefined} download={fileName} data-testid="finished-download" className={`btn-gold px-4 py-3 font-mono-ui text-[11px] uppercase tracking-[0.16em] flex items-center justify-center gap-2 ${url ? "" : "pointer-events-none opacity-50"}`}>
            <Download className="w-4 h-4" /> Download
          </a>
          <button onClick={share} disabled={!url} data-testid="finished-share" className="btn-ghost px-4 py-3 font-mono-ui text-[11px] uppercase tracking-[0.16em] flex items-center justify-center gap-2 disabled:opacity-50">
            <Share2 className="w-4 h-4" /> Share
          </button>
          {!completionist && (
            <button onClick={facebook} data-testid="finished-facebook" className="col-span-2 btn-ghost px-4 py-3 font-mono-ui text-[11px] uppercase tracking-[0.16em] flex items-center justify-center gap-2">
              <Facebook className="w-4 h-4" /> Share the book on Facebook
            </button>
          )}
        </div>
        {!completionist && (
          <Link to={`/reviews?book=${productId}`} data-testid="finished-review" className="mt-5 flex items-center justify-center gap-2 border border-primary-gold/40 bg-primary-gold/5 px-4 py-4 font-mono-ui text-xs uppercase tracking-[0.16em] text-primary-gold hover:bg-primary-gold/10 transition-colors">
            <Star className="w-4 h-4 fill-primary-gold" /> Rate & review {meta?.title || "this book"}
          </Link>
        )}
        <Link to="/reader" data-testid="finished-next-book" className="block text-center mt-5 font-mono-ui text-xs uppercase tracking-[0.18em] text-soft hover:text-primary-gold">{completionist ? "Back to my library →" : "Pick your next book →"}</Link>
      </div>
    </div>
  );
}
